import React, { useState } from 'react';
// React and hooks
import { useHistory } from 'react-router-dom';
import {
  Button,
  Col,
  Collapse,
  Row,
  Space,
  Typography,
  Card,
  Form,
  Select,
  message,
  Menu,
  Dropdown,
  Divider,
  Input,
  Spin,
  Modal,
} from 'antd';

// Shared components
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { validateCampaignOrder } from '../../CampaignRequestor/CampaignRequestorUtil';

// Local components
import { CampaignOrderQueueReviewCampaign } from './CampaignOrderQueueReviewCampaign';
import CampaignLaunchProgressModal from './CampaignLaunchProgressModal';

// Styles
import '../../CampaignRequestor/styles.css';
import './styles.css';
import { getCampaignOrderErrorMessage } from '../../../../core/utils/constants/campaignOrderErrorType';
import { useCampaignOrderQueueContext } from '../../../../core/components/campaigns/campaignordersqueue/useCampaignOrderQueueContext';
import moment from 'moment';
import { formatCampaignName } from '../../../../core/utils/campaignNaming';

const { Panel } = Collapse;
const { Title, Text } = Typography;

export const CampaignOrderQueueCampaignReview = ({ form, fieldName, campaignIndex }) => {
  const { updateCampaignOrder, currentCampaignOrder, loadingUpdateCampaignOrder } =
    useCampaignRequestorContext();
  const { launchCampaignOrderProgrammatic, loadingLaunchCampaingOrderProgrammatic } =
    useCampaignOrderQueueContext();
  const campaign = form.getFieldValue('campaigns')[campaignIndex];
  const [demandSidePlatform, setDemandSidePlatform] = useState(
    campaign ? campaign.demandSidePlatform : null
  );
  const [isManualLaunch, setIsManualLaunch] = useState(false);
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [pendingLaunchStatus, setPendingLaunchStatus] = useState(null);
  const [isAdjustingDate, setIsAdjustingDate] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentLaunchStatus, setCurrentLaunchStatus] = useState(null);

  const history = useHistory();

  const save = async (status) => {
    // Pre-flight campaign order validation
    if (!validateCampaignOrder(currentCampaignOrder, message)) {
      return;
    }

    // Validate form
    try {
      await form.validateFields();
    } catch (error) {
      message.error(error.errorFields[0].errors[0]);
      return;
    }

    try {
      const values = await form.validateFields();

      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: {
            status,
            campaigns: {
              update: values.campaigns.map((campaign) => ({
                where: { id: campaign.id },
                data: {
                  demandSidePlatform: campaign.demandSidePlatform,
                  adProviderId: campaign.adProviderId || null,
                  maxBid: Number(campaign.maxBid || 0),
                  lineItems: {
                    update: campaign.lineItems.map((lineItem) => ({
                      where: { id: lineItem.id },
                      data: {
                        supplySidePlatform: lineItem.supplySidePlatform,
                        maxBid: Number(lineItem.maxBid || 0),
                        frequencyCap: Number(lineItem.frequencyCap || 0),
                        beacons: {
                          create: (lineItem.beacons || [])
                            .filter((beacon, index, array) =>
                              array.findIndex(b => b.beaconId === beacon.beaconId) === index
                            )
                            .map((beacon) => ({
                              beaconId: beacon.beaconId,
                              name: beacon.name,
                              advertiserId: beacon.advertiserId,
                              beaconType: beacon.beaconType,
                            })),
                        },
                        dealIds: {
                          create: lineItem.dealIds.map((data) => ({
                            dealId: data.dealId,
                            name: data.name,
                            org: {
                              connect: {
                                id: currentCampaignOrder.advertiser.id,
                              },
                            },
                          })),
                        },
                      },
                    })),
                  },
                },
              })),
            },
          },
        },
      });
    } catch (error) {
      message.destroy();
      message.error(getCampaignOrderErrorMessage(error));
    }
  };

  const handleLaunchManual = async () => {
    setIsManualLaunch(true);
    
    // Pre-flight campaign order validation
    if (!validateCampaignOrder(currentCampaignOrder, message)) {
      setIsManualLaunch(false);
      return;
    }
    
    try {
      await form.validateFields();
    } catch (error) {
      setIsManualLaunch(false);
      message.warning('Please fix the validation errors before launching.');
      return;
    }

    message.loading('Saving...', 0);
    await save('LIVE');
    history.push('/campaigns/v2/orderqueue/launched');
    message.destroy();
    message.success('Campaign launched successfully');
  };

  const handleLaunchProgrammatic = async (launchStatus = 'pause') => {
    setIsManualLaunch(false);
    
    // Pre-flight campaign order validation
    if (!validateCampaignOrder(currentCampaignOrder, message)) {
      return;
    }
    
    // Validate form
    try {
      await form.validateFields();
    } catch (error) {
      message.error(error.errorFields[0].errors[0]);
      return;
    }

    const { startDate } = currentCampaignOrder;
    const today = moment().startOf('day');
    const campaignStartDate = moment(startDate).startOf('day');

    if (campaignStartDate.isBefore(today)) {
      setPendingLaunchStatus(launchStatus);
      setShowStartDateModal(true);
      return;
    }

    try {
      // Store the launch status for completion handling
      setCurrentLaunchStatus(launchStatus);
      
      // Status update will be handle on the backend
      await save(currentCampaignOrder.status);

      // Navigate to launching tab with a parameter indicating recent launch
      history.push(`/campaigns/v2/orderqueue/launching?launched=${currentCampaignOrder.id}&ts=${Date.now()}`);

      await launchCampaignOrderProgrammatic({
        variables: {
          data: {
            id: currentCampaignOrder.id,
            launchStatus, // pass "play" or "pause"
            alternativeName: formatCampaignName(currentCampaignOrder),
          },
        },
      });

    } catch (error) {
      message.error(getCampaignOrderErrorMessage(error));
    }
  };

  const handleLaunchWithAdjustedDate = async () => {
    if (isAdjustingDate) return;
    
    // Pre-flight campaign order validation
    if (!validateCampaignOrder(currentCampaignOrder, message)) {
      return;
    }
    
    setIsAdjustingDate(true);
    try {
      const today = moment().startOf('day');
      const originalStartDate = moment(currentCampaignOrder.startDate).startOf('day');
      const originalEndDate = moment(currentCampaignOrder.endDate).startOf('day');
      const campaignDurationDays = originalEndDate.diff(originalStartDate, 'days');
      const endDate = today.clone().add(campaignDurationDays, 'days');

      const values = await form.validateFields();

      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: {
            startDate: today.toISOString(),
            endDate: endDate.toISOString(),
            campaigns: {
              update: values.campaigns.map((campaign) => ({
                where: { id: campaign.id },
                data: {
                  startDate: today.toISOString(),
                  endDate: endDate.toISOString(),
                  demandSidePlatform: campaign.demandSidePlatform,
                  adProviderId: campaign.adProviderId || null,
                  maxBid: Number(campaign.maxBid || 4.00),
                  lineItems: {
                    update: campaign.lineItems.map((lineItem) => ({
                      where: { id: lineItem.id },
                      data: {
                        startDate: today.toISOString(),
                        endDate: endDate.toISOString(),
                        supplySidePlatform: lineItem.supplySidePlatform,
                        maxBid: Number(lineItem.maxBid || 0),
                        frequencyCap: Number(lineItem.frequencyCap || 0),
                        beacons: {
                          create: (lineItem.beacons || [])
                            .filter((beacon, index, array) =>
                              array.findIndex(b => b.beaconId === beacon.beaconId) === index
                            )
                            .map((beacon) => ({
                              beaconId: beacon.beaconId,
                              name: beacon.name,
                              advertiserId: beacon.advertiserId,
                              beaconType: beacon.beaconType,
                            })),
                        },
                        dealIds: {
                          create: lineItem.dealIds.map((data) => ({
                            dealId: data.dealId,
                            name: data.name,
                            org: {
                              connect: {
                                id: currentCampaignOrder.advertiser.id,
                              },
                            },
                          })),
                        },
                      },
                    })),
                  },
                },
              })),
            },
          },
        },
      });

      setShowStartDateModal(false);

      const launchStatus = pendingLaunchStatus;
      setPendingLaunchStatus(null);

      try {
        // Store the launch status for completion handling
        setCurrentLaunchStatus(launchStatus);
        
        await save(currentCampaignOrder.status);

        // Navigate to launching tab with a parameter indicating recent launch
        history.push(`/campaigns/v2/orderqueue/launching?launched=${currentCampaignOrder.id}&ts=${Date.now()}`);

        await launchCampaignOrderProgrammatic({
          variables: {
            data: {
              id: currentCampaignOrder.id,
              launchStatus,
              alternativeName: formatCampaignName(currentCampaignOrder),
            },
          },
        });

      } catch (error) {
        message.error(getCampaignOrderErrorMessage(error));
      }
    } catch (error) {
      message.error('Failed to update start date');
    } finally {
      setIsAdjustingDate(false);
    }
  };

  return (
    <Spin spinning={loadingLaunchCampaingOrderProgrammatic || loadingUpdateCampaignOrder}>
      <Collapse bordered={false} defaultActiveKey={[0]}>
        <Panel open={true} header={<Title level={5}>Campaign</Title>}>
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {['APPROVED', 'LIVE_APPROVED', 'LIVE'].includes(currentCampaignOrder.status) && (
                <>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text>DSP</Text>
                        <Text type="secondary">
                          <small> Demand-Side Platform </small>
                        </Text>
                        <Form.Item
                          name={[fieldName, 'demandSidePlatform']}
                          rules={[
                            {
                              required: true,
                              message: 'Please select a DSP',
                            },
                          ]}
                        >
                          <Select
                            style={{ width: '100%' }}
                            showSearch
                            optionFilterProp="children"
                            onChange={(value) => setDemandSidePlatform(value)}
                            filterOption={(input, option) =>
                              option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                            options={[
                              { value: 'NEXXEN', label: 'NEXXEN' },
                              { value: 'LIQUIDM', label: 'LIQUIDM' },
                              { value: 'JAMLOOP', label: 'JAMLOOP' },
                            ]}
                            disabled={currentCampaignOrder.status === 'LIVE'}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                    <Col span={6}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text>Ad Provider ID</Text>
                        <Text type="secondary">
                          <small> Unique identifier for the ad provider </small>
                        </Text>
                        <Form.Item
                          name={[fieldName, 'adProviderId']}
                          rules={[
                            {
                              validator: (_, value) => {
                                if (isManualLaunch && (!value || value.trim() === '')) {
                                  return Promise.reject(
                                    'Ad Provider ID is required for manual launch'
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            style={{ width: '100%' }}
                            onChange={(e) =>
                              form.setFieldsValue({
                                campaigns: form
                                  .getFieldValue('campaigns')
                                  .map((c, idx) =>
                                    idx === campaignIndex
                                      ? { ...c, adProviderId: e.target.value }
                                      : c
                                  ),
                              })
                            }
                            value={(campaign && campaign.adProviderId) || ''}
                            disabled={currentCampaignOrder.status === 'LIVE'}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                    <Col span={7}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text>Maximum Bid</Text>
                        <Text type="secondary">
                          <small> Maximum bid amount for the campaign </small>
                        </Text>
                        <Form.Item
                          name={[fieldName, 'maxBid']}
                          rules={[
                            {
                              required: true,
                              message: 'Maximum bid is required',
                            },
                            {
                              type: 'number',
                              min: 0.01,
                              message: 'Maximum bid must be greater than zero',
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            step="0.01"
                            style={{ width: '100%' }}
                            onChange={(e) => {
                              const value = e.target.value;
                              const numericValue = value === '' ? null : parseFloat(value);
                              form.setFieldsValue({
                                campaigns: form
                                  .getFieldValue('campaigns')
                                  .map((c, idx) =>
                                    idx === campaignIndex
                                      ? { ...c, maxBid: numericValue }
                                      : c
                                  ),
                              });
                            }}
                            value={(campaign && campaign.maxBid) || 4.00}
                            disabled={currentCampaignOrder.status === 'LIVE'}
                          />
                        </Form.Item>
                      </Space>
                    </Col>
                  </Row>
                </>
              )}
              <Form.List name={[fieldName, 'lineItems']}>
                {(fields) => (
                  <>
                    {fields.map(
                      ({ key, name: fieldNameLineItems, fieldKey, ...restField }, index) => (
                        <>
                          <CampaignOrderQueueReviewCampaign
                            key={key}
                            form={form}
                            restField={restField}
                            fieldName={fieldNameLineItems}
                            fieldKey={fieldKey}
                            lineItemIndex={index}
                            campaignIndex={campaignIndex}
                          />
                          <Divider />
                        </>
                      )
                    )}
                  </>
                )}
              </Form.List>

              <>
                {['APPROVED', 'LIVE_APPROVED'].includes(currentCampaignOrder.status) && (
                  <>
                    {demandSidePlatform === 'NEXXEN' ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-end',
                          gap: 12,
                          width: '100%',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Button
                          style={{ width: 140 }}
                          type="default"
                          disabled={loadingUpdateCampaignOrder}
                          onClick={() => handleLaunchManual()}
                        >
                          Launch Manual
                        </Button>
                        <Dropdown
                          trigger={['click']}
                          overlay={
                            <Menu>
                              <Menu.Item onClick={() => handleLaunchProgrammatic('play')}>
                                <span>Launch Auto Play</span>
                              </Menu.Item>
                              <Menu.Item onClick={() => handleLaunchProgrammatic('pause')}>
                                <span>Launch Pause</span>
                              </Menu.Item>
                            </Menu>
                          }
                        >
                          <Button
                            style={{ width: 200 }}
                            type="primary"
                            disabled={
                              loadingUpdateCampaignOrder || loadingLaunchCampaingOrderProgrammatic
                            }
                          >
                            Launch Programmatically
                          </Button>
                        </Dropdown>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                        }}
                      >
                        <Button
                          style={{ width: 100 }}
                          type="primary"
                          disabled={loadingUpdateCampaignOrder}
                          onClick={() => handleLaunchManual()}
                        >
                          Launch
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            </Space>
          </Card>
        </Panel>
      </Collapse>

      <Modal
        title="Start Date Adjustment"
        open={showStartDateModal}
        onOk={handleLaunchWithAdjustedDate}
        onCancel={() => {
          setShowStartDateModal(false);
          setPendingLaunchStatus(null);
        }}
        okText="Yes, adjust and launch"
        cancelText="Cancel"
        confirmLoading={isAdjustingDate}
      >
        <p>
          The start date is behind today's date. We will adjust it to today's date and proceed with
          the launch.
        </p>
        <p>Do you want to continue?</p>
      </Modal>

      <CampaignLaunchProgressModal
        visible={showProgressModal}
        campaignOrderId={currentCampaignOrder?.id}
        campaignName={currentCampaignOrder?.name || 'Campaign'}
        onCancel={() => setShowProgressModal(false)}
        onCompleted={() => {
          setShowProgressModal(false);
          message.success('Campaign launched successfully!');
          // Navigate based on the launch status
          if (currentLaunchStatus === 'pause') {
            history.push('/campaigns/v2/orderqueue/paused');
          } else {
            history.push('/campaigns/v2/orderqueue/launched');
          }
        }}
        onFailed={(progressData) => {
          setShowProgressModal(false);
          const errorMsg = progressData?.metadata?.errorDetails || 'Campaign launch failed';
          message.error(errorMsg);
          // Failed campaigns might remain in launching tab with error status
        }}
        onError={(error) => {
          console.error('Progress subscription error:', error);
          // Keep modal open but show connection error within modal
        }}
      />
    </Spin>
  );
};
